/**
 * @file diff.ts
 * @description «Что изменено» в виде сайта (phase19.16 Р1; SB50, SB60;
 * контракт site-view-api-contract §3, uiux-design-r1 §0.1, §6): сравнение
 * двух снимков по частям и строки для людей — «Кнопки: #ffa915 → #4e5b3a»,
 * «Шрифт заголовков: Montserrat → Lora», «В шапку добавлен пункт «Подарки»».
 *
 * Одна функция для полосы черновика, шторки «Что изменено», окна публикации
 * и истории (версия N против N−1). Изменена ли часть, решает канонический
 * JSON (`changedSiteViewParts`); здесь — только подробности. Часть может
 * считаться изменённой и без строк (например, поменялся порядок ключей в
 * старой ревизии) — тогда кабинет показывает число 0, а публикация всё равно
 * нужна: так безопаснее, чем молча её пропустить.
 *
 * Р2а (db-design-r2a): каталог — по полям словами («Карточка товара:
 * Классика → Текст на фото»); шаблон страницы товара — одной строкой «изменено
 * N блоков» (и «изменён порядок блоков»): по-полевой разбор секций владельцу
 * ничего не говорит, подробности видны на предпросмотре (SB51).
 *
 * SB120 этап 2 (db-design-sb120-stage2 §5): числа оформления — с единицей
 * («×1 → ×1,1», «8 px → 16 px», «18 % → 25 %», «0,08 em → 0,2 em»), «Пилюля»
 * словом; цвет рамки карточек и свечения — одной строкой-подписью; рисунки
 * фона с теми же файлами — «изменены».
 *
 * SB120 этап 4 (db-design-sb120-stage4 §4): вид корзины — «Корзина: …» с
 * единицами, вид и «после «В корзину»» словами по пути (`THEME_VALUE_LABELS`),
 * цвета одной подписью («как шапка», «авто (по заливке)»), скругление «как у
 * сайта»; снимок без раздела сравнивается как умолчание.
 *
 * Партия «Юридические документы» (SB142, db-design-legal-docs §6.1): свои
 * строки «Ссылок» подвала — одной строкой «Нижняя полоса: свои ссылки» со
 * сводкой («нет → 2 строки»); при той же сводке (порядок, другой документ,
 * якорь) — «изменены». Названий документов в снимке нет (только id) —
 * подробности кабинет показывает в самом блоке.
 *
 * Р3г (db-design-r3g §2.3, §4): вид баннера cookie — «Баннер cookie: …»
 * (вид и угол словами, ширина в px, цвета одной подписью, как у корзины);
 * часть COUNTERS — строки `diffSiteCounters` (counters.ts): номера, Вебвизор,
 * режим, вступление, редакция текста платформы.
 *
 * «SEO-техника» v0.19.65 (SB146): часть MENUS — кроме пунктов меню, строки
 * документа навигации `diffSiteNavigation` (navigation.ts): «Хлебные крошки:
 * показывать → не показывать»; снимок без ключа — умолчания.
 */

import { SITE_COLOR_ROLE_INFO, isSiteColorRole } from "./keys.js";
import { PLATFORM_FONT_REGISTRY, type SiteFontRegistry } from "./fonts.js";
import { SITE_CONTACT_KINDS, type SiteContactItem } from "./contacts.js";
import type { SiteMenuLocation, SiteMenuNode, SiteMenus } from "./menu.js";
import { SITE_VIEW_PARTS, type SiteViewPart } from "./parts.js";
import { canonicalJson, type SiteProductTemplateSnapshot, type SiteViewSnapshot } from "./snapshot.js";
import {
  CATALOG_CARD_LOOK_MODE_OPTIONS,
  CATALOG_SORT_LABELS,
  PRODUCT_CARD_TEMPLATES,
  type CatalogCardLook,
  type CatalogSort,
} from "./catalog-options.js";
import type { SiteColorRef } from "./color-ref.js";
import { EYEBROW_FROM_LABEL, HEADING_SCALE_LABEL } from "./font-roles.js";
import { FOOTER_LEGAL_FIELD_LABELS, footerLegalValueLabel, type SiteFooterLegal } from "./footer-legal.js";
import { SURFACE_FILL_BY_TONE, type SiteSurfaceFill } from "./surface-fill.js";
import {
  DEFAULT_SITE_CART,
  SITE_CART_AFTER_ADD_LABELS,
  SITE_CART_MODE_LABELS,
  type SiteCart,
} from "./cart.js";
import type { SiteAutoTextColor } from "./text-color.js";
import { FOOTER_DOCS_DIFF_LABEL, footerDocsSummary, type SiteFooterDocs } from "./footer-docs.js";
import {
  DEFAULT_SITE_CONSENT_BANNER,
  SITE_CONSENT_BANNER_LAYOUT_LABELS,
  SITE_CONSENT_BANNER_SIDE_LABELS,
  type SiteConsentBanner,
} from "./consent-banner.js";
import { diffSiteCounters } from "./counters.js";
import { diffSiteNavigation, readNavigationOptions } from "./navigation.js";

const isFooterLegalField = (key: string): key is keyof SiteFooterLegal => key in FOOTER_LEGAL_FIELD_LABELS;

/** Строка «что изменено». */
export interface SiteViewDiffItem {
  /** Путь в снимке: `theme.colors.button`, `brand.siteName`, `menus.HEADER`. */
  path: string;
  label: string;
  before: string | null;
  after: string | null;
}

export type SiteViewDiff = Record<SiteViewPart, SiteViewDiffItem[]>;

// ── Подписи путей ──

const THEME_LABELS: Readonly<Record<string, string>> = {
  "fonts.heading": "Шрифт заголовков",
  "fonts.body": "Шрифт основного текста",
  "fonts.accent": "Шрифт акцента",
  "type.headingWeight": "Насыщенность заголовков",
  "type.headingCase": "Регистр заголовков",
  "type.headingScale": HEADING_SCALE_LABEL,
  "type.eyebrowFrom": EYEBROW_FROM_LABEL,
  "type.eyebrowTracking": "Разрядка надписей капсом",
  // SB120 этап 2: формы, карточки, свечение и рамка — числами.
  "shapes.buttonRadius": "Скругление кнопок",
  "shapes.buttonArrow": "Стрелка в кнопках",
  "shapes.buttonCase": "Регистр кнопок",
  "shapes.radius.card": "Скругление карточек и фото",
  "shapes.radius.field": "Скругление полей и бейджей",
  "shapes.cards.border.width": "Рамка карточек",
  "shapes.cards.border.color": "Цвет рамки карточек",
  "shapes.cards.shadow": "Тень карточек",
  "backdrop.mode": "Фон страницы",
  "backdrop.haze.strength": "Свечение",
  "backdrop.haze.color": "Цвет свечения",
  "backdrop.images": "Рисунки на фоне страницы",
  "frame.mode": "Раскладка сайта",
  "frame.shadow": "Тень рамки",
  "frame.maxWidth": "Ширина рамки",
  "header.logoPosition": "Место логотипа",
  "header.tone": "Тон шапки",
  "header.fill": "Заливка шапки",
  "header.splitAt": "Пунктов слева от логотипа",
  "header.brandMode": "Бренд в шапке",
  "header.menuCase": "Регистр меню",
  "header.sticky": "Закреплять шапку",
  "header.logoOversize": "Логотип крупнее шапки",
  "footer.template": "Шаблон подвала",
  "footer.tone": "Тон подвала",
  "footer.fill": "Заливка подвала",
  "footer.decorHideOnMobile": "Рисунок подвала на телефоне",
  "spacing.density": "Плотность отступов",
  "spacing.phoneFactor": "Отступы на телефоне, доля",
  // SB120 этап 4: вид корзины (db-design-sb120-stage4 §4).
  "cart.mode": "Корзина: вид",
  "cart.drawer.side": "Корзина: сторона панели",
  "cart.drawer.widthPhone": "Корзина: ширина панели на телефоне",
  "cart.drawer.widthWide": "Корзина: ширина панели от 640 px",
  "cart.drawer.dim": "Корзина: затемнение под панелью",
  "cart.popover.width": "Корзина: ширина окна",
  "cart.popover.rows": "Корзина: строк в окне до прокрутки",
  "cart.popover.dim": "Корзина: затемнение под окном",
  "cart.overlayColor": "Корзина: цвет подложки",
  "cart.fill": "Корзина: заливка",
  "cart.text": "Корзина: цвет текста",
  "cart.summaryFill": "Корзина: заливка «Итого»",
  "cart.totalColor": "Корзина: цвет суммы",
  "cart.radius": "Корзина: скругление",
  "cart.photo": "Корзина: размер фото",
  "cart.afterAdd": "Корзина: после «В корзину»",
  // Р3г: вид баннера согласия на cookie (db-design-r3g §2.3).
  "consentBanner.layout": "Баннер cookie: вид",
  "consentBanner.cardWidth": "Баннер cookie: ширина карточки",
  "consentBanner.cardSide": "Баннер cookie: угол",
  "consentBanner.fill": "Баннер cookie: заливка",
  "consentBanner.text": "Баннер cookie: цвет текста",
};

/** Первая буква строчной: подписи кабинета → значения строки «что изменено». */
const lowerFirst = (text: string): string => text.charAt(0).toLowerCase() + text.slice(1);

const lowerValues = (labels: Readonly<Record<string, string>>): Readonly<Record<string, string>> =>
  Object.fromEntries(Object.entries(labels).map(([key, label]) => [key, lowerFirst(label)]));

/**
 * Значения словами по пути (SB120 этап 4) — отдельно от общего `VALUE_LABELS`,
 * чтобы `open` / `badge` корзины не столкнулись с будущими значениями других
 * полей. Сторона панели берёт общие «слева / справа».
 */
const THEME_VALUE_LABELS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  "cart.mode": lowerValues(SITE_CART_MODE_LABELS),
  "cart.afterAdd": lowerValues(SITE_CART_AFTER_ADD_LABELS),
  "consentBanner.layout": lowerValues(SITE_CONSENT_BANNER_LAYOUT_LABELS),
  // Угол — явным словарём баннера, а не общими «слева / справа»: подписи живут в consent-banner.ts.
  "consentBanner.cardSide": lowerValues(SITE_CONSENT_BANNER_SIDE_LABELS),
};

const FOOTER_SHOW_LABELS: Readonly<Record<string, string>> = {
  brand: "бренд",
  tagline: "слоган",
  contacts: "контакты",
  menu: "меню",
  signature: "подпись",
  decor: "рисунок",
  copyright: "копирайт",
};

const VALUE_LABELS: Readonly<Record<string, string>> = {
  asis: "как написано",
  upper: "капсом",
  body: "шрифт текста",
  heading: "шрифт заголовков",
  normal: "обычная",
  wide: "широкая",
  ultra: "очень широкая",
  square: "прямоугольные",
  rounded: "скруглённые",
  // «Пилюля» — скругление кнопок по высоте (`shapes.buttonRadius: "pill"`, SB120 этап 2).
  pill: "пилюля",
  none: "нет",
  soft: "мягкое",
  large: "большое",
  shadow: "с тенью",
  border: "с рамкой",
  flat: "плоские",
  off: "выключен",
  images: "рисунки",
  full: "во всю ширину",
  boxed: "по центру",
  left: "слева",
  center: "по центру",
  right: "справа",
  above: "над меню",
  light: "светлый",
  dark: "тёмный",
  transparent: "прозрачный",
  logo: "логотип",
  "logo+name": "логотип и название",
  "logo+name+tagline": "логотип, название и слоган",
  name: "название",
  row: "в строку",
  columns: "колонками",
  compact: "компактно",
  spacious: "просторно",
};

const BRAND_LABELS: Readonly<Record<string, string>> = {
  siteName: "Название на сайте",
  tagline: "Слоган",
  logoLightMediaId: "Логотип для светлого фона",
  logoDarkMediaId: "Логотип для тёмного фона",
  faviconMediaId: "Значок вкладки",
  contacts: "Контакты",
  footerSignature: "Подпись подвала",
  footerDecorMediaId: "Рисунок подвала",
  copyrightText: "Копирайт",
  workingHours: "Часы работы",
  deliveryTermsPageId: "Страница «Условия доставки и оплаты»",
};

const SEO_LABELS: Readonly<Record<string, string>> = {
  defaultTitle: "Заголовок по умолчанию",
  titleSuffix: "Окончание заголовков",
  description: "Описание по умолчанию",
  ogImageMediaId: "Картинка для ссылок",
  appShortName: "Короткое название",
  allowIndexing: "Индексация поисковиками",
  "verification.yandex": "Код подтверждения Яндекса",
  "verification.google": "Код подтверждения Google",
};

const BLOG_LABELS: Readonly<Record<string, string>> = {
  eyebrow: "Надзаголовок ленты",
  title: "Заголовок ленты",
  subtitle: "Подзаголовок ленты",
  coverMediaId: "Обложка ленты",
  seoTitle: "Заголовок вкладки ленты",
  seoDescription: "Описание ленты для поиска",
  rssTitle: "Название RSS",
  rssDescription: "Описание RSS",
  // Р2: вид ленты и статьи (blog-options.ts).
  "options.feedHeader": "Шапка ленты блога",
  // Решение 2026-10-12: обложка встроенной шапки ленты.
  "options.coverLayout": "Обложка ленты: как показать",
  "options.coverFocus": "Точка фокуса обложки",
  "options.coverDim": "Затемнение обложки",
  "options.coverTextTone": "Текст на обложке",
  "options.feedLayout": "Вид ленты блога",
  "options.feedCovers": "Обложки в ленте",
  "options.cardFields.category": "Рубрика в карточке статьи",
  "options.cardFields.author": "Автор в карточке статьи",
  "options.cardFields.date": "Дата в карточке статьи",
  "options.cardFields.readTime": "Время чтения в карточке статьи",
  "options.cardFields.excerpt": "Анонс в карточке статьи",
  "options.perPage": "Статей на странице ленты",
  "options.articleLayout": "Шаблон статьи",
  "options.toc.on": "Оглавление статьи",
  "options.toc.place": "Место оглавления",
  "options.share": "Кнопки «Поделиться»",
  "options.authorBlock": "Блок автора",
  "options.related.on": "«Похожие статьи»",
  "options.related.count": "Сколько похожих статей",
};

/** Значения параметров блога словами (у «wide» и «none» здесь свой смысл). */
const BLOG_OPTION_VALUES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  "options.feedHeader": { builtin: "встроенная", custom: "своя" },
  "options.coverLayout": {
    background: "фоном во всю ширину",
    "side-right": "картинкой справа",
    "side-left": "картинкой слева",
  },
  "options.coverTextTone": { light: "светлый", dark: "тёмный" },
  "options.feedLayout": { grid2: "сетка 2", grid3: "сетка 3", list: "список" },
  "options.articleLayout": { wide: "широкая обложка", narrow: "узкая обложка", none: "без обложки" },
  "options.toc.place": { side: "сбоку", top: "над текстом" },
};

/** Р2а: подписи параметров каталога (catalog-options.ts). */
const CATALOG_LABELS: Readonly<Record<string, string>> = {
  "options.card.template": "Карточка товара",
  "options.card.imageAspect": "Пропорции фото в карточке",
  "options.card.fields.badge": "Бейдж в карточке",
  "options.card.fields.shortDescription": "Короткое описание в карточке",
  "options.card.fields.rating": "Рейтинг в карточке",
  "options.card.fields.skuPicker": "Фасовка в карточке",
  "options.card.buyButton": "Кнопка «в корзину» в карточке",
  "options.card.look": "Вид карточки товара",
  "options.grid.desktop": "Товаров в ряд на ПК",
  "options.grid.tablet": "Товаров в ряд на планшете",
  "options.grid.phone": "Товаров в ряд на телефоне",
  "options.perPage": "Товаров на странице",
  "options.sorts.default": "Сортировка по умолчанию",
  "options.sorts.enabled": "Сортировки для покупателя",
  "options.filters.tags": "Фильтр по меткам",
  "options.filters.categoryTree": "Дерево категорий в фильтрах",
  "options.categoryHeader": "Шапка категории",
  // Решение 2026-10-12: как показать баннер категории (по образцу обложки ленты блога).
  "options.categoryBanner.layout": "Баннер категории: как показать",
  "options.categoryBanner.dim": "Затемнение баннера категории",
  "options.categoryBanner.textTone": "Текст на баннере категории",
  "options.subcategoryTiles.on": "Плитки подкатегорий",
  "options.subcategoryTiles.look": "Вид плиток подкатегорий",
  "options.subcategoryTiles.phone": "Плитки подкатегорий на телефоне",
  "options.badgeLabels.BESTSELLER": "Подпись бейджа «бестселлер»",
  "options.badgeLabels.NEW": "Подпись бейджа «новинки»",
  "options.badgeLabels.SALE": "Подпись бейджа «распродажа»",
  "options.badgeLabels.COMING_SOON": "Подпись бейджа «скоро в продаже»",
};

/** Значения параметров каталога словами. */
const CATALOG_OPTION_VALUES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  "options.card.template": Object.fromEntries(PRODUCT_CARD_TEMPLATES.map((t) => [t.key, t.label])),
  "options.card.buyButton": { icon: "значком", text: "текстом", none: "нет" },
  "options.sorts.default": CATALOG_SORT_LABELS,
  "options.categoryHeader": { title: "заголовок", "title-description": "заголовок и описание", banner: "баннер" },
  "options.categoryBanner.layout": {
    background: "фоном во всю ширину",
    "side-right": "фото справа",
    "side-left": "фото слева",
  },
  "options.categoryBanner.textTone": { light: "светлый", dark: "тёмный" },
  "options.subcategoryTiles.look": { overlay: "текст на фото", below: "текст под фото" },
  "options.subcategoryTiles.phone": { scroll: "лентой", grid2: "по две в ряд" },
};

// ── Значения для людей ──

/** Число для людей: десятичная запятая, без лишних нулей (1.125 → «1,125»). */
const ruNumber = (n: number): string => String(n).replace(".", ",");

/**
 * Единицы чисел оформления (SB120 этап 2, db-design-sb120-stage2 §5) — как
 * подписи полей кабинета. «Пилюля» у скругления кнопок — строка, её
 * показывает общий словарь значений.
 */
const THEME_UNITS: Readonly<Record<string, (n: number) => string>> = {
  "type.headingScale": (n) => `×${ruNumber(n)}`,
  "spacing.density": (n) => `×${ruNumber(n)}`,
  "type.eyebrowTracking": (n) => `${ruNumber(n)} em`,
  "shapes.buttonRadius": (n) => `${ruNumber(n)} px`,
  "shapes.radius.card": (n) => `${ruNumber(n)} px`,
  "shapes.radius.field": (n) => `${ruNumber(n)} px`,
  "shapes.cards.border.width": (n) => `${ruNumber(n)} px`,
  "frame.maxWidth": (n) => `${ruNumber(n)} px`,
  "shapes.cards.shadow": (n) => `${ruNumber(n)} %`,
  "frame.shadow": (n) => `${ruNumber(n)} %`,
  "backdrop.haze.strength": (n) => `${ruNumber(n)} %`,
  // SB120 этап 4: корзина — ширины, фото и скругление в px, затемнение в %.
  "cart.drawer.widthPhone": (n) => `${ruNumber(n)} px`,
  "cart.drawer.widthWide": (n) => `${ruNumber(n)} px`,
  "cart.popover.width": (n) => `${ruNumber(n)} px`,
  "cart.photo": (n) => `${ruNumber(n)} px`,
  "cart.radius": (n) => `${ruNumber(n)} px`,
  "cart.drawer.dim": (n) => `${ruNumber(n)} %`,
  "cart.popover.dim": (n) => `${ruNumber(n)} %`,
  "consentBanner.cardWidth": (n) => `${ruNumber(n)} px`,
};

function show(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean") return value ? "да" : "нет";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return VALUE_LABELS[value] ?? value;
  return canonicalJson(value);
}

/** Имя шрифта для людей: каталог платформы или свой шрифт магазина (SB99) по реестру. */
function fontName(key: unknown, fonts: SiteFontRegistry): string | null {
  if (typeof key !== "string") return null;
  return fonts.find(key)?.family ?? key;
}

/** Плоский список листьев объекта: `colors.button` → значение. Массивы — листья. */
function leaves(value: unknown, prefix = ""): Map<string, unknown> {
  const out = new Map<string, unknown>();
  const walk = (v: unknown, path: string): void => {
    if (typeof v === "object" && v !== null && !Array.isArray(v)) {
      for (const [k, nested] of Object.entries(v)) walk(nested, path ? `${path}.${k}` : k);
    } else {
      out.set(path, v);
    }
  };
  walk(value, prefix);
  return out;
}

function themeItem(path: string, before: unknown, after: unknown, fonts: SiteFontRegistry): SiteViewDiffItem {
  const full = `theme.${path}`;
  const [section, key] = path.split(".");
  if (section === "colors" && key && isSiteColorRole(key)) {
    return { path: full, label: SITE_COLOR_ROLE_INFO[key].label, before: show(before), after: show(after) };
  }
  if (section === "fonts") {
    return {
      path: full,
      label: THEME_LABELS[path] ?? path,
      before: before === null ? "нет" : fontName(before, fonts),
      after: after === null ? "нет" : fontName(after, fonts),
    };
  }
  if (section === "footer" && key === "show") {
    const what = FOOTER_SHOW_LABELS[path.split(".")[2] ?? ""] ?? path;
    return { path: full, label: `Подвал: ${what}`, before: show(before), after: show(after) };
  }
  if (section === "footer" && key === "legal") {
    // SB118: значения словами («В одну строку → Построчно»), порядок ссылок — названиями.
    const field = path.split(".")[2] ?? "";
    if (isFooterLegalField(field)) {
      const value = (v: unknown): string | null => footerLegalValueLabel(field, v) ?? show(v);
      return { path: full, label: FOOTER_LEGAL_FIELD_LABELS[field], before: value(before), after: value(after) };
    }
  }
  const words = THEME_VALUE_LABELS[path];
  if (words) {
    const value = (v: unknown): string | null => (typeof v === "string" ? (words[v] ?? v) : show(v));
    return { path: full, label: THEME_LABELS[path] ?? path, before: value(before), after: value(after) };
  }
  const unit = THEME_UNITS[path];
  if (unit) {
    // SB120 этап 2: числа с единицей, как в поле кабинета: «×1 → ×1,1», «8 px → 16 px», «18 % → 25 %».
    const value = (v: unknown): string | null => (typeof v === "number" ? unit(v) : show(v));
    return { path: full, label: THEME_LABELS[path] ?? path, before: value(before), after: value(after) };
  }
  if (path === "spacing.phoneFactor") {
    // Доля — процентами, как в поле кабинета: «60 % → 30 %».
    const percent = (v: unknown): string | null => (typeof v === "number" ? `${Math.round(v * 100)} %` : show(v));
    return { path: full, label: THEME_LABELS[path] ?? path, before: percent(before), after: percent(after) };
  }
  if (path === "backdrop.images") {
    // Без id медиа: число рисунков; при том же числе — «заменены», а при тех же
    // файлах (сдвинули, сменили размер или яркость) — «изменены».
    const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
    const ids = (v: unknown): string =>
      canonicalJson(list(v).map((img) => (typeof img === "object" && img !== null ? (img as { mediaId?: unknown }).mediaId : null)));
    const [b, a] = [list(before).length, list(after).length];
    if (b !== a) return { path: full, label: THEME_LABELS[path] ?? path, before: String(b), after: String(a) };
    const word = ids(before) === ids(after) ? "изменены" : "заменены";
    return { path: full, label: THEME_LABELS[path] ?? path, before: null, after: word };
  }
  return { path: full, label: THEME_LABELS[path] ?? path, before: show(before), after: show(after) };
}

function diffLeaves(
  before: unknown,
  after: unknown,
  item: (path: string, b: unknown, a: unknown) => SiteViewDiffItem,
): SiteViewDiffItem[] {
  const a = leaves(before);
  const b = leaves(after);
  const paths = [...new Set([...a.keys(), ...b.keys()])];
  const items: SiteViewDiffItem[] = [];
  for (const path of paths) {
    const x = a.get(path);
    const y = b.get(path);
    if (canonicalJson(x ?? null) === canonicalJson(y ?? null)) continue;
    items.push(item(path, x ?? null, y ?? null));
  }
  return items;
}

function contactsText(list: unknown): string | null {
  if (!Array.isArray(list) || list.length === 0) return null;
  return (list as SiteContactItem[])
    .map((c) => `${SITE_CONTACT_KINDS[c.kind]?.label ?? c.kind}: ${c.value ?? "почта магазина"}`)
    .join("; ");
}

/** «Картинка фона»: добавлена / убрана / заменена / изменены настройки. */
function pageBgChange(
  before: SiteViewSnapshot["theme"]["backdrop"]["image"],
  after: SiteViewSnapshot["theme"]["backdrop"]["image"],
): SiteViewDiffItem | null {
  if (canonicalJson(before) === canonicalJson(after)) return null;
  const path = "theme.backdrop.image";
  const label = "Картинка фона";
  if (before === null) return { path, label, before: null, after: "добавлена" };
  if (after === null) return { path, label, before: null, after: "убрана" };
  if (before.mediaId !== after.mediaId) return { path, label, before: null, after: "заменена" };
  return { path, label, before: null, after: "изменены настройки" };
}

/** Смена картинки для людей: id медиа в «что изменено» не показываются. */
function mediaChange(before: unknown, after: unknown): string {
  if (before === null || before === undefined) return "добавлена";
  if (after === null || after === undefined) return "убрана";
  return "заменена";
}

function fieldsItem(
  part: "brand" | "seo" | "blog",
  labels: Readonly<Record<string, string>>,
): (path: string, before: unknown, after: unknown) => SiteViewDiffItem {
  return (path, before, after) => {
    const field = path.split(".")[0] ?? path;
    // Картинки — словами, без id медиа: «добавлена / заменена / убрана».
    if (field.endsWith("MediaId")) {
      return { path: `${part}.${field}`, label: labels[field] ?? field, before: null, after: mediaChange(before, after) };
    }
    if (field === "contacts") {
      return { path: `${part}.contacts`, label: labels[field] ?? field, before: contactsText(before), after: contactsText(after) };
    }
    // Вложенные поля (seo.verification.yandex) подписываются по полному пути.
    if (labels[path]) return { path: `${part}.${path}`, label: labels[path]!, before: show(before), after: show(after) };
    return { path: `${part}.${field}`, label: labels[field] ?? field, before: show(before), after: show(after) };
  };
}

// ── Меню ──

const PLACE: Readonly<Record<SiteMenuLocation, { to: string; from: string; of: string }>> = {
  HEADER: { to: "шапку", from: "шапки", of: "шапки" },
  FOOTER: { to: "подвал", from: "подвала", of: "подвала" },
};

interface FlatNode {
  key: string;
  label: string;
  fields: string;
  hidden: boolean;
}

function flatten(nodes: readonly SiteMenuNode[]): FlatNode[] {
  const out: FlatNode[] = [];
  const walk = (list: readonly SiteMenuNode[], parent: string): void => {
    for (const node of list) {
      const label = node.label ?? "(колонка без заголовка)";
      const key = `${parent}/${label}`;
      const { children, isHidden, ...fields } = node;
      out.push({ key, label, fields: canonicalJson(fields), hidden: isHidden });
      walk(children, key);
    }
  };
  walk(nodes, "");
  return out;
}

function diffMenuPlace(location: SiteMenuLocation, before: SiteMenuNode[], after: SiteMenuNode[]): SiteViewDiffItem[] {
  const path = `menus.${location}`;
  const place = PLACE[location];
  const a = flatten(before);
  const b = flatten(after);
  const byKeyA = new Map(a.map((n) => [n.key, n]));
  const byKeyB = new Map(b.map((n) => [n.key, n]));
  const items: SiteViewDiffItem[] = [];
  for (const node of b) {
    const old = byKeyA.get(node.key);
    if (!old) {
      items.push({ path, label: `В ${place.to} добавлен пункт «${node.label}»`, before: null, after: node.label });
      continue;
    }
    if (old.hidden !== node.hidden) {
      items.push({
        path,
        label: node.hidden ? `Пункт «${node.label}» скрыт с сайта` : `Пункт «${node.label}» снова показан`,
        before: old.hidden ? "скрыт" : "показан",
        after: node.hidden ? "скрыт" : "показан",
      });
    }
    if (old.fields !== node.fields) {
      items.push({ path, label: `Изменён пункт «${node.label}»`, before: null, after: null });
    }
  }
  for (const node of a) {
    if (!byKeyB.has(node.key)) {
      items.push({ path, label: `Из ${place.from} удалён пункт «${node.label}»`, before: node.label, after: null });
    }
  }
  const common = (list: FlatNode[], other: Map<string, FlatNode>): string[] =>
    list.filter((n) => other.has(n.key)).map((n) => n.key);
  if (canonicalJson(common(a, byKeyB)) !== canonicalJson(common(b, byKeyA))) {
    items.push({ path, label: `Изменён порядок пунктов ${place.of}`, before: null, after: null });
  }
  return items;
}

function diffMenus(before: SiteMenus, after: SiteMenus): SiteViewDiffItem[] {
  return [
    ...diffMenuPlace("HEADER", before.HEADER, after.HEADER),
    ...diffMenuPlace("FOOTER", before.FOOTER, after.FOOTER),
  ];
}

/** «N блоков» с согласованием числа. */
function blocksWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} блок`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} блока`;
  return `${n} блоков`;
}

/** Шаблон страницы товара: сколько блоков добавлено, убрано, изменено; порядок. */
function diffProductTemplate(before: SiteProductTemplateSnapshot, after: SiteProductTemplateSnapshot): SiteViewDiffItem[] {
  const path = "productTemplate.sections";
  const label = "Шаблон страницы товара";
  const a = new Map(before.sections.map((sec) => [sec.key, canonicalJson(sec)]));
  const b = new Map(after.sections.map((sec) => [sec.key, canonicalJson(sec)]));
  let changed = 0;
  for (const [key, json] of b) if (a.get(key) !== json) changed += 1;
  for (const key of a.keys()) if (!b.has(key)) changed += 1;
  const items: SiteViewDiffItem[] = [];
  if (changed > 0) items.push({ path, label, before: null, after: `изменено ${blocksWord(changed)}` });
  const common = (list: SiteProductTemplateSnapshot["sections"], other: Map<string, string>): string[] =>
    list.filter((sec) => other.has(sec.key)).map((sec) => sec.key);
  if (canonicalJson(common(before.sections, b)) !== canonicalJson(common(after.sections, a))) {
    items.push({ path, label, before: null, after: "изменён порядок блоков" });
  }
  return items;
}

/** Значение параметра каталога словами. */
function catalogValue(path: string, value: unknown): string | null {
  if (path === "options.sorts.enabled" && Array.isArray(value)) {
    return value.map((v) => CATALOG_SORT_LABELS[v as CatalogSort] ?? String(v)).join(", ");
  }
  // Затемнение баннера — доля 0–0,6, владелец видит проценты (как у обложки ленты блога).
  if (path === "options.categoryBanner.dim") return coverDimLabel(value);
  const values = CATALOG_OPTION_VALUES[path];
  if (values && typeof value === "string") return values[value] ?? value;
  // Свои подписи бейджей — как написаны; нет подписи — «как у платформы».
  if (path.startsWith("options.badgeLabels.")) return typeof value === "string" ? value : "как у платформы";
  if (typeof value === "string") return value;
  return show(value);
}

/**
 * Заливка словами: «Фон страницы» (роль) или «свой цвет #3a4b5c». Старая
 * ревизия без `fill` — заливка из её тона (как читает readSiteTheme), чтобы
 * не было ложной строки «— → Фон страницы».
 */
function fillLabel(part: "header" | "footer", section: { tone?: unknown; fill?: unknown }): string | null {
  const byTone = SURFACE_FILL_BY_TONE[part] as Readonly<Record<string, SiteSurfaceFill>>;
  const fill = (section.fill ??
    (typeof section.tone === "string" ? byTone[section.tone] : undefined)) as SiteSurfaceFill | undefined;
  if (!fill) return null;
  if (fill.kind === "custom") return `свой цвет ${fill.color}`;
  return isSiteColorRole(fill.role) ? SITE_COLOR_ROLE_INFO[fill.role].label : String(fill.role);
}

/**
 * Цвет рамки карточек и свечения словами (SB120 этап 2): роль — её
 * название, свой — «свой цвет #…», нет ключа — что берётся «как у сайта».
 */
function colorRefLabel(ref: SiteColorRef | undefined, inherited: string): string {
  if (!ref) return inherited;
  if (ref.kind === "custom") return `свой цвет ${ref.color}`;
  return isSiteColorRole(ref.role) ? SITE_COLOR_ROLE_INFO[ref.role].label : String(ref.role);
}

/**
 * Тема для сравнения: «Картинка фона» убрана (она — отдельной строкой
 * словами), заливки шапки и подвала, цвет рамки карточек и цвет свечения —
 * одним листом-подписью вместо `kind` / `role` / `color`.
 */
function themeForDiff(t: SiteViewSnapshot["theme"]): unknown {
  // Старый снимок в обход readSiteTheme может прийти без новых частей — без подменённых строк.
  const cards = t.shapes.cards as SiteViewSnapshot["theme"]["shapes"]["cards"] | undefined;
  const haze = t.backdrop.haze as SiteViewSnapshot["theme"]["backdrop"]["haze"] | undefined;
  return {
    ...t,
    shapes:
      cards?.border === undefined
        ? t.shapes
        : {
            ...t.shapes,
            cards: { ...cards, border: { ...cards.border, color: colorRefLabel(cards.border.color, "линия (как у сайта)") } },
          },
    backdrop: {
      ...t.backdrop,
      image: undefined,
      haze: haze === undefined ? haze : { ...haze, color: colorRefLabel(haze.color, "акцент (как у сайта)") },
    },
    header: { ...t.header, fill: fillLabel("header", t.header) },
    footer: { ...t.footer, fill: fillLabel("footer", t.footer) },
    cart: cartForDiff(t.cart as Partial<SiteCart> | undefined),
    consentBanner: consentBannerForDiff(t.consentBanner as Partial<SiteConsentBanner> | undefined),
    // Свои строки подвала — отдельной строкой словами (footerDocsChange).
    footerDocs: undefined,
  };
}

/**
 * Свои строки «Ссылок» подвала одной строкой: сводка до и после, при той же
 * сводке — «изменены». Снимок в обход чтения без раздела — «нет» (как его
 * прочитал бы сервер), без падения и без ложной строки.
 */
function footerDocsChange(before: unknown, after: unknown): SiteViewDiffItem | null {
  const items = (v: unknown): SiteFooterDocs["items"] =>
    (v as Partial<SiteFooterDocs> | undefined)?.items ?? [];
  const [b, a] = [items(before), items(after)];
  if (canonicalJson(b) === canonicalJson(a)) return null;
  const [sb, sa] = [footerDocsSummary({ items: b }), footerDocsSummary({ items: a })];
  const path = "theme.footerDocs";
  if (sb !== sa) return { path, label: FOOTER_DOCS_DIFF_LABEL, before: sb, after: sa };
  return { path, label: FOOTER_DOCS_DIFF_LABEL, before: null, after: "изменены" };
}

/**
 * Цвет текста корзины словами: «авто (по заливке)», роль или «свой цвет #…».
 * Нет значения (снимок в обход чтения без `text` / `totalColor`) — умолчание
 * поля, как его подставил бы readSiteTheme: без падения и без ложной строки.
 */
const autoTextLabel = (color: SiteAutoTextColor | undefined, fallback: SiteAutoTextColor): string => {
  const value = color ?? fallback;
  return value.kind === "auto" ? "авто (по заливке)" : colorRefLabel(value, "");
};

/**
 * Корзина для сравнения (SB120 этап 4, §4): цвета — одним листом-подписью
 * («как шапка», «авто (по заливке)», роль, «свой цвет #…»), скругление
 * «как у сайта» словом. Снимок без раздела (в обход readSiteTheme) читается
 * так же, как его прочитал бы сервер, — умолчанием: без падения и без
 * ложных строк «— → панель сбоку».
 */
function cartForDiff(input: Partial<SiteCart> | undefined): unknown {
  const cart = input ?? DEFAULT_SITE_CART;
  const D = DEFAULT_SITE_CART;
  // Нет ключа — умолчание поля (как при чтении); null — осознанный выбор.
  const fill = cart.fill === undefined ? D.fill : cart.fill;
  const radius = cart.radius === undefined ? D.radius : cart.radius;
  return {
    ...cart,
    overlayColor: colorRefLabel(cart.overlayColor ?? D.overlayColor, ""),
    fill: fill === null ? "как шапка" : colorRefLabel(fill, ""),
    text: autoTextLabel(cart.text, D.text),
    summaryFill: colorRefLabel(cart.summaryFill ?? D.summaryFill, ""),
    totalColor: autoTextLabel(cart.totalColor, D.totalColor),
    radius: radius ?? "как у сайта",
  };
}

/**
 * Баннер cookie для сравнения (Р3г): цвета — одним листом-подписью, как у
 * корзины. Снимок без раздела (в обход readSiteTheme) — умолчание, без
 * ложных строк «— → полоса внизу».
 */
function consentBannerForDiff(input: Partial<SiteConsentBanner> | undefined): unknown {
  const banner = { ...DEFAULT_SITE_CONSENT_BANNER, ...(input ?? {}) };
  return {
    ...banner,
    fill: colorRefLabel(banner.fill, ""),
    text: autoTextLabel(banner.text, DEFAULT_SITE_CONSENT_BANNER.text),
  };
}

/** Тон шапки/подвала производный от заливки: при смене заливки строка тона — лишний шум. */
function dropDerivedTones(items: SiteViewDiffItem[]): SiteViewDiffItem[] {
  const changed = new Set(items.map((i) => i.path));
  return items.filter((item) => {
    const part = item.path === "theme.header.tone" ? "header" : item.path === "theme.footer.tone" ? "footer" : null;
    if (part === null || !changed.has(`theme.${part}.fill`)) return true;
    // Прозрачная шапка — отдельный режим, её включение/выключение показываем.
    return item.before === VALUE_LABELS["transparent"] || item.after === VALUE_LABELS["transparent"];
  });
}

/**
 * «Вид карточки товара» словами одной строкой: «как у всех карточек сайта»
 * или «свои: скругление 8 px, рамка 1 px, тень 50 %» (числами с SB120 этапа
 * 2). Старая ревизия без ключа — «как у всех» (как читает readCatalogOptions),
 * без ложной строки.
 */
function cardLookLabel(look: CatalogCardLook | undefined): string {
  if (!look || look.mode === "site") return CATALOG_CARD_LOOK_MODE_OPTIONS.site.toLowerCase();
  const { border, shadow } = look.cards;
  const radius = look.radius === 0 ? "без скругления" : `скругление ${ruNumber(look.radius)} px`;
  const frame = border.width === 0 ? "без рамки" : `рамка ${ruNumber(border.width)} px`;
  const color = border.width === 0 ? "" : ` (${colorRefLabel(border.color, "линия").toLowerCase()})`;
  const tint = shadow === 0 ? "без тени" : `тень ${ruNumber(shadow)} %`;
  return `${CATALOG_CARD_LOOK_MODE_OPTIONS.own.toLowerCase()}: ${radius}, ${frame}${color}, ${tint}`;
}

/**
 * Точка фокуса обложки ленты словами: «по центру» или «по горизонтали 30 %,
 * по вертикали 70 %». Одна строка вместо двух (`coverFocus.x` / `.y`):
 * владелец двигает точку целиком.
 */
function coverFocusLabel(focus: { x: number; y: number } | undefined): string | null {
  if (!focus) return null;
  if (focus.x === 50 && focus.y === 50) return "по центру";
  return `по горизонтали ${focus.x} %, по вертикали ${focus.y} %`;
}

/** Затемнение обложки ленты и баннера категории: доля 0–0,6 хранится, владелец видит проценты. */
function coverDimLabel(value: unknown): string | null {
  return typeof value === "number" ? `${Math.round(value * 100)} %` : show(value);
}

function blogForDiff(b: SiteViewSnapshot["blog"]): unknown {
  // Старый снимок в обход readSiteViewSnapshot может прийти без ключа — тогда без подменённой строки.
  const options = b.options as typeof b.options | undefined;
  if (!options) return b;
  return { ...b, options: { ...options, coverFocus: coverFocusLabel(options.coverFocus) } };
}

function catalogForDiff(c: SiteViewSnapshot["catalog"]): unknown {
  const card = c.options.card as typeof c.options.card | undefined;
  if (!card) return c;
  return { ...c, options: { ...c.options, card: { ...card, look: cardLookLabel(card.look) } } };
}

/**
 * Подробности различий по всем частям: `before` — опубликованное (или
 * умолчания), `after` — черновик. Части без изменений — пустые массивы.
 * `fonts` — реестр шрифтов магазина: свой шрифт показывается своим именем
 * («Montserrat → Marianna»), а не ключом `shop:<id>` (Р1-7, SB99).
 */
export function diffSiteView(
  before: SiteViewSnapshot,
  after: SiteViewSnapshot,
  fonts: SiteFontRegistry = PLATFORM_FONT_REGISTRY,
): SiteViewDiff {
  const result = Object.fromEntries(SITE_VIEW_PARTS.map((p) => [p, [] as SiteViewDiffItem[]])) as SiteViewDiff;
  // «Картинка фона» — одной строкой словами (без id медиа и по-полевых строк).
  result.THEME = dropDerivedTones(
    diffLeaves(themeForDiff(before.theme), themeForDiff(after.theme), (path, b, a) => themeItem(path, b, a, fonts)),
  );
  const bgItem = pageBgChange(before.theme.backdrop.image, after.theme.backdrop.image);
  if (bgItem) result.THEME.push(bgItem);
  const docsItem = footerDocsChange(before.theme.footerDocs, after.theme.footerDocs);
  if (docsItem) result.THEME.push(docsItem);
  // contacts — массив, сравнивается целиком одним листом.
  result.BRAND = diffLeaves(before.brand, after.brand, fieldsItem("brand", BRAND_LABELS));
  result.SEO = diffLeaves(before.seo, after.seo, fieldsItem("seo", SEO_LABELS));
  result.MENUS = [
    ...diffMenus(before.menus, after.menus),
    // SB146: документ навигации — второй ключ части MENUS.
    ...diffSiteNavigation(readNavigationOptions(before.navigation), readNavigationOptions(after.navigation)),
  ];
  const blogItem = fieldsItem("blog", BLOG_LABELS);
  result.BLOG = diffLeaves(blogForDiff(before.blog), blogForDiff(after.blog), (path, b, a) => {
    if (path === "options.coverDim") {
      return { path: `blog.${path}`, label: BLOG_LABELS[path] ?? path, before: coverDimLabel(b), after: coverDimLabel(a) };
    }
    const values = BLOG_OPTION_VALUES[path];
    if (!values) return blogItem(path, b, a);
    const word = (v: unknown): string | null => (typeof v === "string" ? (values[v] ?? v) : show(v));
    return { path: `blog.${path}`, label: BLOG_LABELS[path] ?? path, before: word(b), after: word(a) };
  });
  result.CATALOG = diffLeaves(catalogForDiff(before.catalog), catalogForDiff(after.catalog), (path, b, a) => ({
    path: `catalog.${path}`,
    label: CATALOG_LABELS[path] ?? path,
    before: catalogValue(path, b),
    after: catalogValue(path, a),
  }));
  result.PRODUCT_TEMPLATE = diffProductTemplate(before.productTemplate, after.productTemplate);
  // Р3г: снимок в обход readSiteViewSnapshot может прийти без части — сравнение не падает.
  if (before.counters !== undefined && after.counters !== undefined) {
    result.COUNTERS = diffSiteCounters(before.counters, after.counters);
  }
  return result;
}
